import { useInvalidate } from '@refinedev/core';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * E57 · the client page re-reads what an action changed (review F8, F9). Its reads are Refine `useCustom` queries keyed by
 * their GraphQL operation; Finish Session (`upsertSession` through the data provider's `custom`) and Assign homework
 * (the drawer's own mutation) bypass Refine's resource invalidation, so the header kept "No session yet", the row its old
 * counts, the digest its old cut, until a reload.
 */
export const AFTER_SESSION: readonly string[] = ['clientHeader', 'clientToolNavigation', 'clientDigest'];
export const AFTER_ASSIGN: readonly string[] = ['clientToolNavigation', 'clientDigest', 'clientHomeworkPage'];

/** Is this query key a custom read of one of these operations (the operation rides the key's params object)? */
export const isReadOf = (queryKey: QueryKey, operations: readonly string[]): boolean =>
  queryKey.some(
    (part) =>
      !!part &&
      typeof part === 'object' &&
      operations.includes((part as { operation?: unknown }).operation as string),
  );

export const useRefreshClientPage = () => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  return useCallback(
    (what: 'session' | 'assign') => {
      const operations = what === 'session' ? AFTER_SESSION : AFTER_ASSIGN;
      void queryClient.invalidateQueries({ predicate: (q) => isReadOf(q.queryKey, operations) });
      // Sessions & notes (the embedded sessions list) is a Refine resource list
      if (what === 'session') void invalidate({ resource: 'sessions', invalidates: ['list'] });
    },
    [queryClient, invalidate],
  );
};
